// @ts-nocheck
import Clickable from "@insite/mobius/Clickable";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import get from "@insite/mobius/utilities/get";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
// import styled, { withTheme } from "styled-components"; // Store Custom Code

/* Store Custom Code - Start */
import styled, { css, withTheme } from "styled-components";
import { createPortal } from "react-dom";

const useOverlayDefaultValue = true;

export type TooltipDirection = "top" | "right" | "bottom" | "left";
const defaultDirection: TooltipDirection = "top";
const defaultOffset = 2;
const oppositeSide: Record<TooltipDirection, TooltipDirection> = {
    top: "bottom",
    bottom: "top",
    left: "right",
    right: "left",
};

type TooltipContainerStyledProps = InjectableCss<any> &
    Pick<TooltipProps, "useOverlay" | "direction" | "offset" | "interactive">;

const getContainerDirectionCss = (direction: TooltipDirection = defaultDirection, offset: number = defaultOffset) => {
    const anchor = oppositeSide[direction];
    const isVertical = direction === "top" || direction === "bottom";
    return css`
        ${anchor}: 100%;
        margin-${anchor}: ${offset}px;

        /* Transparent bridge filling the gap so the pointer can cross from the trigger onto the tooltip
           without passing over a dead zone. It only matters when the container is interactive
           (pointer-events: auto); otherwise it is inert like the rest of the tooltip. */
        &::before {
            content: "";
            position: absolute;
            ${anchor}: -${offset}px;
            ${
                isVertical
                    ? css`
                          left: 0;
                          right: 0;
                          height: ${offset}px;
                      `
                    : css`
                          top: 0;
                          bottom: 0;
                          width: ${offset}px;
                      `
            }
        }

        ${
            isVertical
                ? css`
                      left: 50%;
                      transform: translateX(-50%);
                  `
                : css`
                      width: auto;
                      max-width: 220px;
                      top: 50%;
                      transform: translateY(-50%);
                  `
        }
    `;
};

type TooltipArrowStyledProps = InjectableCss<any> & Pick<TooltipProps, "direction">;

const getArrowDirectionCss = (direction: TooltipDirection = defaultDirection) => {
    const isVertical = direction === "top" || direction === "bottom";
    return css`
        ${direction}: 100%;
        border-${direction}-color: ${getColor("common.backgroundContrast")};
        ${
            isVertical
                ? css`
                      left: 50%;
                      transform: translateX(-50%);
                  `
                : css`
                      top: 50%;
                      transform: translateY(-50%);
                  `
        }
    `;
};
/* Store Custom Code - End */

export interface TooltipPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS strings or styled-components functions to be injected into nested components. These will override the theme defaults.
     * @themable */
    cssOverrides?: {
        tooltipClickable?: StyledProp<TooltipProps>;
        tooltipBody?: StyledProp<TooltipProps>;
        tooltipContainer?: StyledProp<TooltipProps>;
        tooltipWrapper?: StyledProp<TooltipProps>;
        tooltipArrow?: StyledProp<TooltipProps>;
    };
    /** Props to be passed into the trigger Icon component if no triggerComponent is provided.
     * @themable */
    iconProps?: IconPresentationProps;
    /** Props that will be passed to the typography component rendered in the tooltip.
     * @themable */
    typographyProps?: TypographyPresentationProps;
    // Store Custom Code
    useOverlay?: boolean;
    /** The gap in pixels between the tooltip and the trigger. Defaults to 11. */
    offset?: number;
    /** The side the tooltip opens toward relative to the trigger. Defaults to "top". */
    direction?: TooltipDirection;
    /** When true, the tooltip stays open while the pointer is hovering over it (bridging the gap to the
     * trigger). When false (default), the tooltip closes as soon as the pointer leaves the trigger,
     * even if it is over the tooltip. */
    interactive?: boolean;
}

export type TooltipComponentProps = MobiusStyledComponentProps<
    "span",
    {
        /** Component to be used as the trigger to optionally replace the default trigger icon. */
        triggerComponent?: React.ReactElement<{ tabIndex: number }>;
        /** The text that will appear in the tooltip */
        text: string | React.ReactElement; // Store Custom Code
        /** Function called on tooltip open */
        onOpen?: React.EventHandler<React.SyntheticEvent>;
        /** Function called on tooltip close */
        onClose?: React.EventHandler<React.SyntheticEvent>;
        /** Text to be used to describe the tooltip trigger. Used for screenreaders. Defaults to "more info" */
        triggerAltText?: string | null; // Store Custom Code
        /** Optionally make the tooltip also display during on mouse hover */
        displayOnHover?: boolean;
        // Starter Custom Code
        /** Text to be used for the title of the Clickable. */
        clickableTitle?: string;
        // Store Custom Code
        triggerIsClickable?: boolean;
    }
>;

export type TooltipProps = TooltipComponentProps & TooltipPresentationProps;

const ESC_KEY = 27;
const ENTER_KEY = 13;

const TooltipContainer = styled.div<TooltipContainerStyledProps /* Store Custom Code */>`
    width: 220px;
    position: ${x => (x.useOverlay ? "fixed" : "absolute") /* Store Custom Code */};
    display: inline-flex;
    justify-content: center;
    /* Store Removed - bottom */
    /* Store Removed - left */
    /* Store Removed - transform */
    /* Store Removed - margin-bottom */
    /* Store Custom Code - Start */
    pointer-events: ${({ interactive }) => (interactive ? "auto" : "none")};
    ${({ direction, offset }) => getContainerDirectionCss(direction, offset)}
    ${({ useOverlay }) => {
        if (useOverlay) {
            return `z-index: 999999;`;
        }
    }}
    /* Store Custom Code - End */
    ${injectCss}
`;

const TooltipBody = styled.div<InjectableCss<any>>`
    display: inline;
    padding: 10px;
    text-align: center;
    min-width: 100px;
    border-radius: 5px;
    background: ${getColor("common.backgroundContrast")};
    color: ${getColor("common.background")};
    ${injectCss}
`;

const TooltipWrapper = styled.div<InjectableCss<any>>`
    position: relative;
    display: inline-block;
    ${injectCss}
`;

// necessary to use theme clickable styles and instance clickable overrides
const TooltipClickable = styled(Clickable)`
    padding: 0 1px 3px;
    ${injectCss}
`;

const TooltipArrow = styled.div<TooltipArrowStyledProps /* Store Custom Code */>`
    content: "";
    position: absolute;
    /* Store Removed - top */
    /* Store Removed - left */
    /* Store Removed - transform */
    border-width: 6px;
    border-style: solid;
    border-color: transparent; /* Store Custom Code */
    /* Store Custom Code - Start */
    pointer-events: none;
    ${({ direction }) => getArrowDirectionCss(direction)}
    /* Store Custom Code - End */
    ${injectCss}
`;

/**
 * Component that provides supplementatal information on click or enter.
 */
class Tooltip extends React.Component<TooltipProps> {
    state = { visible: false };

    element = React.createRef<HTMLDivElement>();

    tooltipElement = React.createRef<HTMLDivElement>();

    tooltipArrow = React.createRef<HTMLDivElement>();

    /* Store Custom Code - Start */
    /** Synchronous mirror of state.visible, used to avoid stale-state races on rapid hover in/out. */
    isVisible = false;

    /** Pending re-open timer id from the accessibility close/re-open cycle. */
    reopenTimeout: ReturnType<typeof setTimeout> | undefined;
    /* Store Custom Code - End */

    componentDidMount() {
        document.addEventListener("mousedown", this.handleClick);
        /* Store Custom Code - Start */
        document.addEventListener("scroll", this.handlePosition, true);
        window.addEventListener("resize", this.handlePosition);
        /* Store Custom Code - End */
    }

    componentWillUnmount() {
        document.removeEventListener("mousedown", this.handleClick);
        /* Store Custom Code - Start */
        document.removeEventListener("scroll", this.handlePosition, true);
        window.removeEventListener("resize", this.handlePosition);
        this.clearReopenTimeout();
        /* Store Custom Code - End */
    }

    /* Store Custom Code - Start */
    componentDidUpdate() {
        /** Keep the open tooltip anchored to its trigger across re-renders. When the trigger performs
         * its own action (e.g. a keyboard-activated collapse toggle), the surrounding layout re-renders
         * and the portalled tooltip node is remounted, which discards the imperatively-set position
         * styles. positionTooltip only runs on open, so without this the tooltip stays open but drifts
         * off-screen. Re-running it after each update while visible re-anchors it to the trigger. */
        if (this.state.visible) {
            this.positionTooltip();
        }
    }
    /* Store Custom Code - End */

    openTooltip = (event: React.SyntheticEvent) => {
        event.preventDefault();
        /* Store Custom Code - Start */
        this.clearReopenTimeout();
        /** For accssibility reasons, the tooltip can only be closed by blur or escape, if the tooltip is open,
         * it will close and then re-open after a discrete interval. */
        if (this.isVisible) {
            this.closeTooltip(event);
            this.reopenTimeout = setTimeout(() => {
                this.reopenTimeout = undefined;
                this.showTooltip();
            }, 100);
        } else {
            this.showTooltip();
            this.props.onOpen && this.props.onOpen(event);
        }
        /* Store Custom Code - End */
    };

    /* Store Custom Code - Start */
    showTooltip = () => {
        this.isVisible = true;
        this.setState({ visible: true }, this.positionTooltip);
    };

    clearReopenTimeout = () => {
        if (this.reopenTimeout) {
            clearTimeout(this.reopenTimeout);
            this.reopenTimeout = undefined;
        }
    };

    /** Keep the tooltip open when the pointer enters either the trigger or the tooltip itself. */
    handleMouseEnter = (event: React.SyntheticEvent) => {
        if (!this.isVisible) {
            this.openTooltip(event);
        }
    };

    /** Close immediately, unless the pointer is moving onto the tooltip or back onto the trigger.
     * When the tooltip is not interactive it is pointer-events: none, so it can never be `relatedTarget`
     * and this check simply falls through to a close. The transparent gap bridge keeps `relatedTarget`
     * pointing at the tooltip while crossing the gap, so no close delay is needed. */
    handleMouseLeave = (event: React.MouseEvent) => {
        const related = event.relatedTarget as Node | null;
        if (related && (this.tooltipElement.current?.contains(related) || this.element.current?.contains(related))) {
            return;
        }
        this.closeTooltip(event);
    };

    handleTriggerClick = (event: React.MouseEvent) => {
        // When the trigger performs its own action, a pointer click should dismiss the tooltip
        // rather than (re)open it — otherwise it can stay open after the pointer leaves.
        // Keyboard activation (Enter/Space) also fires a click but reports detail === 0; in that case
        // keep the tooltip open so keyboard users retain the context.
        if (this.props.triggerIsClickable) {
            if (event.detail !== 0) {
                this.closeTooltip(event);
            }
            return;
        }
        this.openTooltip(event);
    };
    /* Store Custom Code - End */

    positionTooltip = () => {
        if (IS_SERVER_SIDE) {
            return;
        }
        const tooltip = this.tooltipElement.current;
        const hoverTrigger = this.element.current;
        const arrow = this.tooltipArrow.current;
        if (tooltip && hoverTrigger && arrow) {
            const toolTipRect = tooltip.getBoundingClientRect();
            const hoverTriggerRect = hoverTrigger.getBoundingClientRect();
            const arrowRect = arrow.getBoundingClientRect();
            /* Store Custom Code - Start */
            const direction = this.props.direction ?? defaultDirection;
            /** The arrow is a CSS-border triangle that overhangs the container toward the trigger by half
             * its bounding box. Add that overhang to the anchor so the offset gap is measured to the arrow
             * tip rather than the container body — otherwise a larger arrow eats into the gap and can
             * overlap the trigger. */
            const arrowOverhangX = arrowRect.width / 2;
            const arrowOverhangY = arrowRect.height / 2;
            if (this.props.useOverlay ?? useOverlayDefaultValue) {
                tooltip.style.top = "auto";
                tooltip.style.bottom = "auto";
                tooltip.style.left = "auto";
                tooltip.style.right = "auto";
                switch (direction) {
                    case "bottom":
                        tooltip.style.left = `${hoverTriggerRect.left + hoverTriggerRect.width / 2}px`;
                        tooltip.style.top = `${hoverTriggerRect.bottom + arrowOverhangY}px`;
                        break;
                    case "right":
                        tooltip.style.left = `${hoverTriggerRect.right + arrowOverhangX}px`;
                        tooltip.style.top = `${hoverTriggerRect.top + hoverTriggerRect.height / 2}px`;
                        break;
                    case "left":
                        tooltip.style.right = `${
                            document.documentElement.clientWidth - hoverTriggerRect.left + arrowOverhangX
                        }px`;
                        tooltip.style.top = `${hoverTriggerRect.top + hoverTriggerRect.height / 2}px`;
                        break;
                    case "top":
                    default:
                        tooltip.style.left = `${hoverTriggerRect.left + hoverTriggerRect.width / 2}px`;
                        tooltip.style.bottom = `${
                            document.documentElement.clientHeight - hoverTriggerRect.top + arrowOverhangY
                        }px`;
                        break;
                }

                return;
            }

            // Horizontal-edge correction only applies to vertically-stacked tooltips.
            if ((direction === "top" || direction === "bottom") && toolTipRect.x < 0) {
                tooltip.style.left = `0`;
                tooltip.style.transform = `translateX(${-hoverTriggerRect.x}px)`;
                arrow.style.left = `0`;
                arrow.style.transform = `translateX(${hoverTriggerRect.x + arrowRect.width / 4}px)`;
            }
            /* Store Custom Code - End */
        }
    };

    closeTooltip = (event: React.SyntheticEvent) => {
        this.clearReopenTimeout(); // Store Custom Code
        if (this.isVisible /* Store Custom Code */) {
            this.isVisible = false; // Store Custom Code
            this.setState({ visible: false });
            this.props.onClose && this.props.onClose(event);
        }
        // eslint-disable-next-line no-useless-return
        return;
    };

    handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.keyCode === ESC_KEY) {
            event.stopPropagation();
            this.closeTooltip(event);
        }
        if (event.keyCode === ENTER_KEY && !this.props.triggerIsClickable /* Store Custom Code */) {
            event.stopPropagation();
            this.openTooltip(event);
        }
    };

    /* Store Custom Code - Start */
    handleClick = (event: MouseEvent) => {
        if (this.state.visible) {
            const isTooltip = this.tooltipElement.current === event?.target;
            const isInsideTooltip = this.tooltipElement.current?.contains(event?.target as Node);

            if (!isTooltip && !isInsideTooltip) {
                this.closeTooltip(event as any);
            }
        }
    };

    handleBlur = (event: React.FocusEvent) => {
        if (this.state.visible) {
            setTimeout(() => {
                // A body target indicates a blur caused by a non-interactible target
                const isTargetInteractible = document.activeElement !== document.body;
                // The blur points to itself for certain interactions (e.g.: changing browser tabs)
                const isTargetSelf = document.activeElement === event.target;

                if (isTargetInteractible && !isTargetSelf) {
                    const isTooltip = this.tooltipElement.current === document.activeElement;
                    const isInsideTooltip = this.tooltipElement.current?.contains(document.activeElement);

                    if (!isTooltip && !isInsideTooltip) {
                        this.closeTooltip(event as any);
                    }
                }
            });
        }
    };

    handlePosition = (event: Event) => {
        if (this.state.visible) {
            this.closeTooltip(event as any);
        }
    };
    /* Store Custom Code - End */

    render() {
        const {
            text,
            triggerComponent,
            triggerAltText,
            iconProps,
            mergeCss,
            displayOnHover,
            clickableTitle, // Starter Custom Code
            /* Store Custom Code - Start */
            as,
            useOverlay = useOverlayDefaultValue,
            triggerIsClickable,
            direction = defaultDirection,
            offset = defaultOffset,
            interactive = false,
            /* Store Custom Code - End */
            ...otherProps
        } = this.props;
        if (!text /* Store Custom Code */) {
            return null;
        }
        const { spreadProps } = applyPropBuilder(this.props, { component: "tooltip" });
        const { applyStyledProp } = applyPropBuilder(this.props, { component: "tooltip" });
        const resolvedMergeCss = mergeCss ?? otherProps?.theme?.tooltip?.defaultProps?.mergeCss;
        let trigger;

        if (triggerComponent) {
            /** remove tabability because of `Clickable` wrapper to remove redundant tab if the triggerComponent
             * is a tabbable component. */
            trigger = React.cloneElement(triggerComponent, triggerIsClickable ? {} : { tabIndex: -1 }); // Store Custom Code
        } else {
            trigger = <IconMemo {...spreadProps("iconProps")} />;
        }

        const toolTipComponent = (
            <TooltipContainer
                data-id="tooltipContainer"
                css={applyStyledProp(["cssOverrides", "tooltipContainer"], resolvedMergeCss)}
                ref={this.tooltipElement}
                /* Store Custom Code - Start */
                useOverlay={useOverlay}
                direction={direction}
                offset={offset}
                interactive={interactive}
                onMouseEnter={displayOnHover ? this.handleMouseEnter : undefined}
                onMouseLeave={displayOnHover ? this.handleMouseLeave : undefined}
                /* Store Custom Code - End */
                {...otherProps}
            >
                <TooltipArrow
                    ref={this.tooltipArrow}
                    css={applyStyledProp(["cssOverrides", "tooltipArrow"], resolvedMergeCss)}
                    // Store Custom Code
                    direction={direction}
                />
                <TooltipBody
                    data-id="tooltipBody"
                    css={applyStyledProp(["cssOverrides", "tooltipBody"], resolvedMergeCss)}
                >
                    <Typography
                        as={typeof text === "string" ? "p" : "div"} // Store Custom Code
                        {...spreadProps("typographyProps")}
                        data-test-selector="toolTip"
                    >
                        {text}
                    </Typography>
                </TooltipBody>
            </TooltipContainer>
        );

        /* Store Custom Code - Start */
        const TooltipPortal = () => {
            return createPortal(toolTipComponent, document.getElementById("tooltip-portal") || document.body);
        };
        /* Store Custom Code - End */

        let hoverProps;
        if (displayOnHover) {
            hoverProps = {
                /* Store Custom Code - Start */
                onMouseEnter: this.handleMouseEnter,
                onMouseLeave: this.handleMouseLeave,
                onFocus: this.handleMouseEnter,
                /* Store Custom Code - End */
            };
        }
        return (
            <TooltipWrapper
                ref={this.element}
                css={applyStyledProp(["cssOverrides", "tooltipWrapper"], resolvedMergeCss)}
            >
                <TooltipClickable
                    forwardedAs={triggerIsClickable ? as || "span" : as} // Store Custom Code
                    css={applyStyledProp(["cssOverrides", "tooltipClickable"], resolvedMergeCss)}
                    onClick={this.handleTriggerClick} // Store Custom Code
                    onKeyDown={this.handleKeyDown}
                    onBlur={this.handleBlur} // Store Custom Code
                    data-test-selector="tooltip_OpenTooltip"
                    {...hoverProps}
                    title={clickableTitle || triggerAltText || undefined} // Store Custom Code
                    // Store Custom Code
                    tabIndex={triggerIsClickable ? -1 : undefined}
                >
                    {trigger}
                    {triggerAltText !== null && !triggerIsClickable /* Store Custom Code */ && (
                        <VisuallyHidden>{triggerAltText || this.props.theme!.translate("more info")}</VisuallyHidden>
                    )}
                </TooltipClickable>
                {!useOverlay /* Store Custom Code */ && (
                    <div aria-live="polite" data-test-selector="tooltip_TooltipInfo">
                        {this.state.visible ? toolTipComponent : null}
                    </div>
                )}
                {useOverlay && this.state.visible && <TooltipPortal /> /* Store Custom Code */}
            </TooltipWrapper>
        );
    }
}

/** @component */
export default withTheme(Tooltip);
